<script setup lang="ts">
import DeliveryMethod from '@/components/Settings/DeliveryMethod.vue'
import EnrichmentFilters from '@/components/Settings/EnrichmentFilters.vue'
import ProfileSettings from '@/components/Settings/ProfileSettings.vue'
import OGHeader from '@/components/OGHeader.vue'
import { useAuthStore } from '@/stores/authStore'
import { ref, type Ref } from 'vue'
import { useRouter } from 'vue-router'
import ConnectedCalendars from '@/components/Settings/ConnectedCalendars.vue'
import { myRequest } from '@/utils/fetch'

const authStore = useAuthStore()
const router = useRouter()

const loading = ref(false)
const emailDelivery = ref(false)
const calendarDelivery = ref(false)
const enrichmentFilters = ref({
  currentPosition: true,
  jobHistory: true,
  education: true,
  companyOverview: true,
  companyNews: true,
  companyFunding: true,
  weather: true,
  talkingPoints: true,
  location: true
})
const notifyAt = ref('10:00')
const notifyBefore: Ref<number | null> = ref(null)
const lastMetWith = ref(7)
const deliveryRule = ref('always')

const saveSettings = async () => {
  loading.value = true
  const isOnboarding = !authStore.settings
  const setting = await myRequest('POST', '/user/settings', {
    emailDelivery: emailDelivery.value,
    calendarDelivery: calendarDelivery.value,
    enrichmentFilters: enrichmentFilters.value,
    notifyAt: notifyAt.value.length === 5 ? `${notifyAt.value}:00` : notifyAt.value,
    notifyBefore: notifyBefore.value,
    lastMetWith: lastMetWith.value,
    deliveryRule: deliveryRule.value
  })
  setSettingsRefs(setting)
  await authStore.checkWithServer()
  if (isOnboarding) router.push('/')
  loading.value = false
}
const getSettings = async () => {
  loading.value = true
  const setting = await myRequest('GET', '/user/settings')
  if (setting) setSettingsRefs(setting)
  loading.value = false
}
const setSettingsRefs = (setting: {
  settingID: number
  userID: number
  emailDelivery: boolean
  calendarDelivery: boolean
  enrichmentFilters: {
    currentPosition: boolean
    jobHistory: boolean
    education: boolean
    companyOverview: boolean
    companyNews: boolean
    companyFunding: boolean
    weather: boolean
    talkingPoints: boolean
    location: boolean
  }
  notifyAt: string
  notifyBefore: number
  lastMetWith: number
  deliveryRule: string
}) => {
  emailDelivery.value = setting.emailDelivery
  calendarDelivery.value = setting.calendarDelivery
  enrichmentFilters.value = setting.enrichmentFilters
  notifyAt.value = setting.notifyAt
  notifyBefore.value = setting.notifyBefore
  lastMetWith.value = setting.lastMetWith
  deliveryRule.value = setting.deliveryRule
}
getSettings()
</script>

<template>
  <OGHeader title="Settings" subtitle="Manage your account settings" />

  <div class="row q-col-gutter-md full-width q-mb-md">
    <div class="col-12 col-md paddingFixerSingle">
      <ProfileSettings />
    </div>
    <div class="col-12 col-md paddingFixerSingle">
      <ConnectedCalendars :calendar-delivery="calendarDelivery" />
    </div>
  </div>

  <div class="q-mb-md">
    <DeliveryMethod
      v-model:calendarDelivery="calendarDelivery"
      v-model:emailDelivery="emailDelivery"
      v-model:notifyBefore="notifyBefore"
      v-model:lastMetWith="lastMetWith"
      v-model:deliveryRule="deliveryRule"
    />
  </div>

  <div class="q-mb-md">
    <EnrichmentFilters v-model:enrichmentFilters="enrichmentFilters" />
  </div>

  <!-- Save button row -->
  <div class="row justify-center q-mt-lg">
    <q-btn
      :disable="loading"
      :loading="loading"
      color="primary"
      label="Save"
      class="col-12 col-md-8"
      @click="saveSettings"
    />
  </div>
</template>
